"use client";

import { motion } from "framer-motion";
import Link from "next/link";

export interface AboutProjectBlockStyle {
    backgroundColor?: string;
    titleColor?: string;
    textColor?: string;
    labelColor?: string;
    accentColor?: string; // For the large number
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
    paddingTop?: string;
    paddingBottom?: string;
}

export interface AboutProjectBlockProps {
    title: string;
    description: string;
    industry: string;
    durationValue: string;
    durationUnit: string;
    services: string[];
    buttonText?: string;
    buttonLink?: string;
    style?: AboutProjectBlockStyle;
}

export default function AboutProjectBlock({
    title,
    description,
    industry,
    durationValue,
    durationUnit,
    services = [],
    buttonText = "Let's chat 👋",
    buttonLink = "#",
    style
}: AboutProjectBlockProps) {
    return (
        <div
            className="w-full"
            style={{
                backgroundColor: style?.backgroundColor || "#FFFFFF",
                paddingTop: style?.paddingTop || "6rem",
                paddingBottom: style?.paddingBottom || "6rem",
                color: style?.textColor || "#1F2937"
            }}
        >
            <div className="container mx-auto px-4 md:px-12 lg:px-24">
                {/* Main Title */}
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-4xl md:text-5xl font-medium tracking-tight mb-16 md:mb-24"
                    style={{ color: style?.titleColor || "#000000" }}
                >
                    {title}
                </motion.h2>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">

                    {/* Left Column: Metrics & Info */}
                    <div className="lg:col-span-5 grid grid-cols-2 gap-y-12 gap-x-8">
                        {/* Industry */}
                        {industry && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="col-span-2 md:col-span-1"
                            >
                                <h4 className="text-sm font-medium mb-3" style={{ color: style?.labelColor || "#9CA3AF" }}>Industry</h4>
                                <p className="text-xl md:text-2xl font-medium">{industry}</p>
                            </motion.div>
                        )}

                        {/* Spacer for grid alignment if Industry is present, or conditional empty slot */}
                        <div className="hidden md:block col-span-1"></div>

                        {/* Duration */}
                        {durationValue && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="col-span-1"
                            >
                                <h4 className="text-sm font-medium mb-1" style={{ color: style?.labelColor || "#9CA3AF" }}>Duration</h4>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-6xl md:text-7xl font-bold font-serif leading-none" style={{ color: style?.titleColor || "#000000" }}>
                                        {durationValue}
                                    </span>
                                    {durationUnit && (
                                        <span className="text-sm font-medium" style={{ color: style?.labelColor || "#9CA3AF" }}>
                                            {durationUnit}
                                        </span>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {/* Services - Only show if has items */}
                        {services && services.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="col-span-1"
                            >
                                <h4 className="text-sm font-medium mb-3" style={{ color: style?.labelColor || "#9CA3AF" }}>Services</h4>
                                <div className="flex flex-col gap-1">
                                    {services.map((service, idx) => (
                                        <span key={idx} className="text-lg font-medium leading-relaxed">
                                            {service || ""}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Right Column: Description & CTA */}
                    <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-between">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mb-12 relative"
                        >
                            <div
                                className="text-xl md:text-2xl leading-relaxed font-normal"
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                        >
                            <Link
                                href={buttonLink}
                                className="inline-flex items-center justify-center px-8 py-3 rounded-full text-base font-medium transition-transform hover:scale-105"
                                style={{
                                    backgroundColor: style?.buttonBackgroundColor || "#F3F4F6",
                                    color: style?.buttonTextColor || "#1F2937"
                                }}
                            >
                                {buttonText}
                            </Link>
                        </motion.div>
                    </div>

                </div>
            </div>
        </div>
    );
}
